import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import NavBaseTemplate from '../templates/NavBaseTemplate';
import DataTable from '../theme/tables/DataTable';
import FeedbackTile from '../theme/modals/FeedbackTile';
import UberSubmitErrorModal from '../uber/UberSubmitErrorModal';
import api from '../../store/api/api';
import { useUberRides, RIDE_STATUS } from '../uber/useUberRides';
import UberRideInfoModal from '../uber/UberRideInfoModal';

const FILTERED_STATUSES = [RIDE_STATUS.CANCELED];
const REFRESH_INTERVAL = 20000; // 20 seconds

function formatPickup(ride) {
  if (!ride.pickup_date) return '–';

  const date = new Date(ride.pickup_date + 'T00:00:00');
  const dateStr = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

  if (ride.is_flexible_pickup) {
    return `${dateStr} (Flexible)`;
  }

  if (!ride.pickup_time) {
    return dateStr;
  }

  const [h, m] = ride.pickup_time.split(':').map(Number);
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? 'pm' : 'am';

  return `${dateStr} ${hour12}:${m.toString().padStart(2, '0')}${ampm}`;
}

function formatLocation(loc) {
  if (!loc) return '–';

  const type = (loc.type || loc.location_type || '').toLowerCase();
  const label = loc.label || loc.custom_address || '–';

  if (type === 'office') return `Office: ${label}`;
  if (type === 'site') return `Site: ${label}`;
  if (type === 'custom' || type === 'address') return `Address: ${label}`;
  return '–';
}

function formatStatus(status) {
  if (!status) return '';
  return status.replace(/_/g, ' ');
}

function UberRidesScreen({ focusedCampaign }) {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayAll, setDisplayAll] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const refreshIntervalRef = useRef(null);

  const [screenError, setScreenError] = useState(null);

  const {
    cancelRide,
    submitRide,
    approveRide,
    infoModal,
    fetchRideInfo,
    closeRideInfo,
  } = useUberRides(null, false);

  const getDateOptions = () => {
    const options = [];
    const today = new Date();

    for (let i = 0; i < 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const displayStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      options.push({ value: dateStr, label: displayStr });
    }

    return options;
  };

  const loadRides = useCallback(async () => {
    if (!focusedCampaign) return;

    setLoading(true);

    try {
      const response = await api.get('/uber/ride-requests/', {
        campaign_id: focusedCampaign.id,
        date: selectedDate,
      });
      setRides(response.data);
    } catch (err) {
      console.error('Failed to load Uber rides', err);
      alert('Error loading Uber rides. Check console.');
    } finally {
      setLoading(false);
    }
  }, [focusedCampaign, selectedDate]);

  // Silent wholistic refresh — no loading indicator, replaces the full rides list
  const refreshRides = useCallback(async () => {
    if (!focusedCampaign) return;

    try {
      const response = await api.get('/uber/ride-requests/', {
        campaign_id: focusedCampaign.id,
        date: selectedDate,
      });
      setRides(response.data);
    } catch (err) {
      console.error('Failed to refresh Uber rides', err);
    }
  }, [focusedCampaign, selectedDate]);

  // Load rides when campaign or date changes
  useEffect(() => {
    loadRides();
  }, [loadRides]);

  // 20-second wholistic refresh interval
  useEffect(() => {
    if (!focusedCampaign) return;

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(() => {
      refreshRides();
    }, REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [focusedCampaign, selectedDate, refreshRides]);

  const isReadyForUber = (ride) => {
    return (ride.status === RIDE_STATUS.APPROVED && ride.total_seat_count === ride.total_assigned_seats)
        || ride.status === RIDE_STATUS.FAILED;
  };

  const handleApprove = async (ride) => {
    setLoadingAction(`approve-${ride.id}`);
    try {
      await approveRide(ride.id, 'current_user');
      const response = await api.get('/uber/ride-requests/', { ride_request_id: ride.id });
      if (response.data?.[0]) {
        const updatedRide = response.data[0];
        setRides(prev => prev.map(r => r.id === ride.id ? updatedRide : r));
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSubmit = async (ride) => {
    setLoadingAction(`submit-${ride.id}`);
    try {
      await submitRide(ride.id);
      const response = await api.get('/uber/ride-requests/', { ride_request_id: ride.id });
      if (response.data?.[0]) {
        const updatedRide = response.data[0];
        setRides(prev => prev.map(r => r.id === ride.id ? updatedRide : r));
      }
    } catch (err) {
      setScreenError(err.message || 'An error occurred while submitting the ride to Uber.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCancel = async (ride) => {
    setLoadingAction(`cancel-${ride.id}`);
    try {
      await cancelRide(ride.id);
      const response = await api.get('/uber/ride-requests/', { ride_request_id: ride.id });
      if (response.data?.[0]) {
        const updatedRide = response.data[0];
        if (updatedRide.status === RIDE_STATUS.CANCELED || updatedRide.status === RIDE_STATUS.RIDER_CANCELED) {
          setRides(prev => prev.filter(r => r.id !== ride.id));
          setCancelSuccess(true);
          setTimeout(() => setCancelSuccess(false), 3000);
        } else {
          setRides(prev => prev.map(r => r.id === ride.id ? updatedRide : r));
        }
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const buildTableData = () => {
    const filteredRides = displayAll
        ? rides
        : rides.filter(ride => !FILTERED_STATUSES.includes(ride.status));

    return filteredRides.map((ride) => {
      const isCompleted = ride.status === RIDE_STATUS.UBER_COMPLETED;
      const canApprove = ride.status === RIDE_STATUS.PENDING_APPROVAL && !isCompleted;
      const canSubmit = !isCompleted && isReadyForUber(ride);
      const canCancel = ![
        RIDE_STATUS.UBER_ACTIVE,
        RIDE_STATUS.UBER_COMPLETED,
        RIDE_STATUS.UBER_CANCELED,
        RIDE_STATUS.CANCELED,
        RIDE_STATUS.RIDER_CANCELED
      ].includes(ride.status);
      const isActionLoading = loadingAction && loadingAction.endsWith(`-${ride.id}`);

      const isApproveLoading = loadingAction === `approve-${ride.id}`;
      const isSubmitLoading = loadingAction === `submit-${ride.id}`;
      const isCancelLoading = loadingAction === `cancel-${ride.id}`;

      return {
        pickup_time: formatPickup(ride),
        primary_rider: ride.primary_rider || '–',
        seats: (ride.seats || [])
            .filter(seat => !seat.is_primary_rider)
            .map(seat => seat.canvasser_name)
            .join(', ') || '–',
        pickup: formatLocation(ride.pickup),
        dropoff: formatLocation(ride.dropoff),
        status: (
            <span className={`uber-status-badge uber-status-${ride.status}`}>
              {formatStatus(ride.status)}
            </span>
        ),
        controls: (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <i
                  className="fa fa-info-circle"
                  style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '1.2rem' }}
                  title="View ride info"
                  onClick={() => fetchRideInfo(ride.id)}
              />
              {isApproveLoading ? (
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: '1.2rem', color: '#28a745' }} />
              ) : (
                  <i
                      className="fa fa-check-circle"
                      style={{
                        color: canApprove ? '#28a745' : '#ccc',
                        cursor: canApprove && !isActionLoading ? 'pointer' : 'not-allowed',
                        fontSize: '1.2rem'
                      }}
                      title="Approve"
                      onClick={() => canApprove && !isActionLoading && handleApprove(ride)}
                  />
              )}
              {isSubmitLoading ? (
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: '1.2rem', color: '#28a745' }} />
              ) : (
                  <i
                      className="fa fa-paper-plane"
                      style={{
                        color: canSubmit ? '#28a745' : '#ccc',
                        cursor: canSubmit && !isActionLoading ? 'pointer' : 'not-allowed',
                        fontSize: '1.2rem'
                      }}
                      title="Submit to Uber"
                      onClick={() => canSubmit && !isActionLoading && handleSubmit(ride)}
                  />
              )}
              {isCancelLoading ? (
                  <i className="fa fa-spinner fa-spin" style={{ fontSize: '1.2rem', color: '#dc3545' }} />
              ) : (
                  <i
                      className="fa fa-times-circle"
                      style={{
                        color: canCancel ? '#dc3545' : '#ccc',
                        cursor: canCancel && !isActionLoading ? 'pointer' : 'not-allowed',
                        fontSize: '1.2rem'
                      }}
                      title="Cancel"
                      onClick={() => canCancel && !isActionLoading && handleCancel(ride)}
                  />
              )}
            </div>
        ),
        _data: ride,
      };
    });
  };

  return (
      <NavBaseTemplate>
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label>
            <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ marginRight: '0.5rem' }}
            >
              {getDateOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
              ))}
            </select>
          </label>
          <label>
            <input
                type="checkbox"
                checked={displayAll}
                onChange={(e) => setDisplayAll(e.target.checked)}
            />
            {' '}Display all trips
          </label>
        </div>

        <DataTable
            title="Uber Rides"
            data={buildTableData()}
            columns={[
              { key: 'pickup_time', label: 'Pickup Time' },
              { key: 'primary_rider', label: 'Primary Rider' },
              { key: 'seats', label: 'Seats' },
              { key: 'pickup', label: 'Pickup' },
              { key: 'dropoff', label: 'Dropoff' },
              { key: 'status', label: 'Status' },
              { key: 'controls', label: 'Controls' },
            ]}
        />

        {!loading && rides.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              There are no Uber rides for this day.
            </div>
        )}

        <FeedbackTile text="Loading Uber rides…" visible={loading} />

        <FeedbackTile text="Ride cancelled successfully." visible={cancelSuccess} classNames="success" />

        <UberSubmitErrorModal
            show={!!screenError}
            errorMessage={screenError}
            onClose={() => setScreenError(null)}
        />

        <UberRideInfoModal
            show={infoModal?.show}
            rideInfo={infoModal?.data}
            loading={infoModal?.loading}
            onClose={closeRideInfo}
        />
      </NavBaseTemplate>
  );
}

const mapStateToProps = (state) => ({
  focusedCampaign: state.session.focusedCampaign,
});

export default connect(mapStateToProps)(UberRidesScreen);